import React, { useState, useEffect, useCallback, useRef } from "react";
import { API_URL } from "../../utils/config";
import { formatNumero } from "../../utils/Utilitats";
import DetallsPartidesPlantilla from "./DetallsPartidesPlantilla";

function PartidesPlantilla({ idEmpresa, plantilla, onTornar }) {
  const [partidesList, setPartidesList] = useState([]);
  const [expandedIds, setExpandedIds] = useState([]);
  const [selectedPartida, setSelectedPartida] = useState(null);
  const [activeTab, setActiveTab] = useState("table");
  const [nivellSeleccionat, setNivellSeleccionat] = useState(0);

  const selectedRowRef = useRef(null);

  const compararPartida = (a, b) => {
    const sa = (a || "").split(".").map(Number);
    const sb = (b || "").split(".").map(Number);
    for (let i = 0; i < Math.max(sa.length, sb.length); i++) {
      const na = sa[i] || 0;
      const nb = sb[i] || 0;
      if (na !== nb) return na - nb;
    }
    return 0;
  };

  const fetchPartides = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/Partidesplantilla.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idPlantilla: plantilla.id, idEmpresa }),
      });
      const data = await resp.json();
      if (data.success) {
        const sorted = (data.partides || []).sort((a, b) =>
          compararPartida(a.Partida, b.Partida)
        );
        setPartidesList(sorted);
        return sorted;
      }
    } catch (err) {
      console.error("Error carregant partides:", err);
    }
    return [];
  }, [plantilla.id]);

  useEffect(() => {
    fetchPartides();
  }, [fetchPartides]);

  // Calcular nivell seleccionat (com Versions.js)
  useEffect(() => {
    if (!selectedPartida) {
      setNivellSeleccionat(0);
    } else {
      let nivell = 0;
      let partidaActual = selectedPartida;
      while (partidaActual) {
        const idPare = partidaActual.idPare;
        if (idPare == null) break;
        const pare = partidesList.find(
          (p) => parseInt(p.id) === parseInt(idPare)
        );
        if (!pare) break;
        nivell++;
        partidaActual = pare;
      }
      if (expandedIds.includes(parseInt(selectedPartida.id))) {
        nivell++;
      }
      setNivellSeleccionat(nivell);
    }
  }, [selectedPartida, expandedIds, partidesList]);

  // Scroll a la fila seleccionada
  useEffect(() => {
    if (activeTab !== "table") return;
    if (selectedRowRef.current) {
      selectedRowRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selectedPartida, activeTab]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && activeTab === "table") {
        onTornar();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [activeTab, onTornar]);

  const calcularSeguentPartida = (idPare) => {
    const siblings = partidesList.filter((p) => {
      const pare = p.idPare ? parseInt(p.idPare) : null;
      return pare === (idPare ? parseInt(idPare) : null);
    });
    if (idPare) {
      const pare = partidesList.find((p) => parseInt(p.id) === parseInt(idPare));
      const prefix = pare ? pare.Partida : "";
      return `${prefix}.${String(siblings.length + 1).padStart(3, "0")}`;
    }
    return String(siblings.length + 1).padStart(3, "0");
  };

  const getNivellText = () => {
    if (!selectedPartida) return "/";
    const isExpanded = expandedIds.includes(parseInt(selectedPartida.id));
    let pareId = isExpanded ? parseInt(selectedPartida.id) : (selectedPartida.idPare ? parseInt(selectedPartida.idPare) : null);
    if (pareId === null) return "/";
    const chain = [];
    let currentId = pareId;
    while (currentId) {
      const p = partidesList.find(x => parseInt(x.id) === parseInt(currentId));
      if (!p) break;
      const tipus = p.EsApartat ? "Apartat" : "Partida";
      const desc = p.DescripcioPartida ? ` ${p.DescripcioPartida.substring(0, 30)}` : "";
      chain.unshift(`${tipus} ${p.Partida}${desc}`);
      currentId = p.idPare ? parseInt(p.idPare) : null;
    }
    return chain.join(" / ");
  };

  // Funcions auxiliars (com Versions.js)
  function esParent(list, possibleParentId, childId) {
    let currentId = childId;
    while (currentId) {
      if (parseInt(currentId) === parseInt(possibleParentId)) return true;
      const child = list.find((p) => parseInt(p.id) === parseInt(currentId));
      currentId = child?.idPare || null;
    }
    return false;
  }

  function hiHaPartides(idPare) {
    return partidesList.some(
      (p) =>
        (p.idPare ? parseInt(p.idPare) : null) === idPare &&
        parseInt(p.EsApartat) !== 1
    );
  }

  function hiHaApartats(idPare) {
    return partidesList.some(
      (p) =>
        (p.idPare ? parseInt(p.idPare) : null) === idPare &&
        parseInt(p.EsApartat) === 1
    );
  }

  function partidesFills(idPare) {
    return partidesList.filter(
      (p) => (p.idPare ? parseInt(p.idPare) : null) === idPare
    );
  }

  const handleRowClick = (f) => {
    if (selectedPartida && parseInt(selectedPartida.id) === parseInt(f.id)) {
      setSelectedPartida(null);
    } else {
      setSelectedPartida(f);
    }
  };

  const toggleExpand = (e, f) => {
    e.stopPropagation();
    const id = parseInt(f.id);
    const isExpanded = expandedIds.includes(id);
    if (isExpanded) {
      if (
        selectedPartida &&
        (parseInt(selectedPartida.id) === id ||
          esParent(partidesList, id, parseInt(selectedPartida.id)))
      ) {
        setSelectedPartida(null);
      }
      setExpandedIds((prev) => prev.filter((x) => x !== id));
    } else {
      setExpandedIds((prev) => [...prev, id]);
    }
  };

  // Llista plana per navegació
  const flatPartides = (() => {
    const result = [];
    const addChildren = (parentId) => {
      const children = partidesList
        .filter((p) => (p.idPare ? parseInt(p.idPare) : null) === parentId)
        .sort((a, b) => compararPartida(a.Partida, b.Partida));
      children.forEach((c) => {
        result.push(c);
        addChildren(parseInt(c.id));
      });
    };
    addChildren(null);
    return result;
  })();

  const reFetchPartides = async () => {
    const prevId = selectedPartida?.id || null;
    const list = await fetchPartides();
    setActiveTab("table");
    if (prevId && list.length > 0) {
      const updated = list.find((p) => parseInt(p.id) === parseInt(prevId));
      if (updated) setSelectedPartida(updated);
      else setSelectedPartida(null);
    }
  };

  // =========================================================================
  //  BARRA RESUM: Total hores MO, Cost MO, Cost Materials
  // =========================================================================
  const timeFactorToHours = {
    Minuts: 1 / 60,
    Hores: 1,
    Dies: 8,
    Setmanes: 40,
    Mesos: 160,
  };

  const calcularTotalsPartida = (partida) => {
    const isApartat = parseInt(partida.EsApartat) === 1;
    const quant = parseFloat(partida.Quantitat) || 0;

    let horesUnit = 0;
    let costMOUnit = 0;
    let costMatUnit = 0;

    if (!isApartat) {
      const duracioUnit = parseFloat(partida.DuracioEstimadaUnitat) || 0;
      const factor = timeFactorToHours[partida.UnitatsTemps] || 1;
      horesUnit = duracioUnit * factor;

      const preuGrup = parseFloat(partida.PreuGrupMaObra) || 0;
      costMOUnit = horesUnit * preuGrup;

      const preuMaterial = parseFloat(partida.PreuMinimMaterial) || 0;
      const qtatProd = parseFloat(partida.QuantitatProd) || 1;
      costMatUnit = preuMaterial * qtatProd;
    }

    const fills = partidesList.filter(
      (p) => (p.idPare ? parseInt(p.idPare) : null) === parseInt(partida.id)
    );

    let horesFills = 0;
    let costMOFills = 0;
    let costMatFills = 0;

    fills.forEach((child) => {
      const t = calcularTotalsPartida(child);
      horesFills += t.totalHores;
      costMOFills += t.totalCostMO;
      costMatFills += t.totalCostMaterials;
    });

    return {
      totalHores: (horesUnit + horesFills) * quant,
      totalCostMO: (costMOUnit + costMOFills) * quant,
      totalCostMaterials: (costMatUnit + costMatFills) * quant,
    };
  };

  const calcularResumPlantilla = () => {
    const roots = partidesList.filter(
      (p) => (p.idPare ? parseInt(p.idPare) : null) === null
    );

    let totalHores = 0;
    let totalCostMO = 0;
    let totalCostMaterials = 0;

    roots.forEach((root) => {
      const t = calcularTotalsPartida(root);
      totalHores += t.totalHores;
      totalCostMO += t.totalCostMO;
      totalCostMaterials += t.totalCostMaterials;
    });

    return {
      totalHores: Math.round(totalHores * 100) / 100,
      totalCostMO: Math.round(totalCostMO * 100) / 100,
      totalCostMaterials: Math.round(totalCostMaterials * 100) / 100,
    };
  };

  const renderResum = () => {
    const resum = calcularResumPlantilla();
    const costTotal = Math.round((resum.totalCostMO + resum.totalCostMaterials) * 100) / 100;

    return (
      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginTop: "5px" }}>
        <div>
          <strong>Total Mà d'Obra:</strong> {formatNumero(resum.totalHores)} h
        </div>
        <div>
          <strong>Cost Mà d'Obra:</strong> {formatNumero(resum.totalCostMO)}€
        </div>
        <div>
          <strong>Cost Materials:</strong> {formatNumero(resum.totalCostMaterials)}€
        </div>
        <div>
          <strong>Cost Aproximat:</strong> {formatNumero(costTotal)}€
        </div>
      </div>
    );
  };

  // =========================================================================
  //  RENDER ARBRE RECURSIU (com Versions.js)
  // =========================================================================
  function renderArbre(idPare, level) {
    const fills = partidesList
      .filter((p) => (p.idPare ? parseInt(p.idPare) : null) === idPare)
      .sort((a, b) => compararPartida(a.Partida, b.Partida));

    if (fills.length === 0) {
      return (
        <div style={{ padding: "10px" }}>No hi ha elements en aquest nivell.</div>
      );
    }

    const primer = fills[0];
    const isNivellApartats = parseInt(primer.EsApartat) === 1;

    if (isNivellApartats) {
      return (
        <div style={{ padding: "5px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {fills.map((f) => {
                const id = parseInt(f.id);
                const isExpanded = expandedIds.includes(id);
                const isSelected = selectedPartida && parseInt(selectedPartida.id) === id;

                return (
                  <React.Fragment key={id}>
                    <tr
                      ref={isSelected ? selectedRowRef : null}
                      onClick={() => handleRowClick(f)}
                      onDoubleClick={() => {
                        setSelectedPartida(f);
                        setActiveTab("consultar");
                      }}
                      style={{
                        cursor: "pointer",
                        backgroundColor: isSelected ? "#eee" : "transparent",
                        borderLeft: isSelected
                          ? "2px solid #007cba"
                          : "2px solid transparent",
                      }}
                    >
                      <td style={{ paddingLeft: level * 20, padding: "5px" }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                        >
                          <span>
                            <span
                              style={{ marginRight: "5px" }}
                              onClick={(e) => toggleExpand(e, f)}
                            >
                              {isExpanded ? "▼" : "▶"}
                            </span>
                            <strong>{f.Partida}</strong> - {f.DescripcioPartida}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td
                          colSpan={1}
                          style={{
                            backgroundColor: "#fafafa",
                            paddingLeft: level * 20 + 20,
                          }}
                        >
                          {renderArbre(id, level + 1)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    } else {
      if (
        idPare === null ||
        esParent(
          partidesList,
          idPare,
          selectedPartida ? parseInt(selectedPartida.id) : null
        )
      ) {
        return (
          <div style={{ marginTop: "5px" }}>
            <table className="Dashboard-table" style={{ tableLayout: "auto" }}>
              <thead>
                <tr>
                  <th style={{ width: "30px" }}></th>
                  <th className="text-left">Partida</th>
                  <th className="text-right">Quantitat</th>
                  <th className="text-center">Unitats</th>
                  <th className="text-left">Descripció</th>
                  <th className="text-left">Material/Producte</th>
                  <th className="text-left">Grup M.O.</th>
                  <th className="text-left">Duració</th>
                  <th className="text-right">Preu Mat.</th>
                </tr>
              </thead>
              <tbody>
                {fills.map((f) => {
                  const id = parseInt(f.id);
                  const isExpanded = expandedIds.includes(id);
                  const isSelected =
                    selectedPartida && parseInt(selectedPartida.id) === id;

                  return (
                    <React.Fragment key={id}>
                      <tr
                        ref={isSelected ? selectedRowRef : null}
                        className={isSelected ? "selected-row" : ""}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleRowClick(f)}
                        onDoubleClick={() => {
                          setSelectedPartida(f);
                          setActiveTab("consultar");
                        }}
                      >
                        <td
                          style={{ textAlign: "center" }}
                          onClick={(e) => toggleExpand(e, f)}
                        >
                          {isExpanded ? "▼" : "▶"}
                        </td>
                        <td className="text-left">
                          <strong>{f.Partida}</strong>
                        </td>
                        <td className="text-right">
                          {formatNumero(f.Quantitat)}
                        </td>
                        <td className="text-center">{f.Unitats}</td>
                        <td className="text-left">
                          {f.DescripcioPartida || ""}
                        </td>
                        <td className="text-left">
                          {f.NomMaterial ||
                            `${f.MarcaProducte || ""} ${f.ModelProducte || ""}`.trim() ||
                            ""}
                        </td>
                        <td className="text-left">{f.NomGrupMaObra || ""}</td>
                        <td className="text-left">
                          {f.DuracioEstimadaUnitat && f.Quantitat
                            ? `${formatNumero(parseFloat(f.DuracioEstimadaUnitat) * parseFloat(f.Quantitat))} ${f.UnitatsTemps || ""}`
                            : ""}
                        </td>
                        <td className="text-right">
                          {f.PreuMinimMaterial != null && f.Quantitat
                            ? `${formatNumero(parseFloat(f.PreuMinimMaterial) * parseFloat(f.Quantitat) * parseFloat(f.QuantitatProd || 1))}€`
                            : ""}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td
                            colSpan={9}
                            style={{ backgroundColor: "#fafafa" }}
                          >
                            {renderArbre(id, level + 1)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      } else {
        return (
          <div style={{ padding: "10px" }}>
            <div className="info-grid header">
              <div className="info-col strong">Partida</div>
              <div className="info-col strong numeric">Quantitat</div>
              <div className="info-col strong center">Unitats</div>
              <div className="info-col strong">Descripció</div>
              <div className="info-col strong">Material</div>
              <div className="info-col strong">Grup M.O.</div>
            </div>
            {fills.map((sp) => (
              <div key={sp.id} className="info-grid">
                <div className="info-col">{sp.Partida}</div>
                <div className="info-col numeric">
                  {formatNumero(sp.Quantitat)}
                </div>
                <div className="info-col center">{sp.Unitats}</div>
                <div className="info-col">{sp.DescripcioPartida}</div>
                <div className="info-col">
                  {sp.NomMaterial ||
                    `${sp.MarcaProducte || ""} ${sp.ModelProducte || ""}`.trim()}
                </div>
                <div className="info-col">{sp.NomGrupMaObra || ""}</div>
              </div>
            ))}
          </div>
        );
      }
    }
  }

  const renderArbreNivell0 = () => {
    if (partidesList.length === 0) {
      return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <p>No hi ha partides (ni apartats) per aquesta plantilla.</p>
          <div className="Dashboard-button-group" style={{ marginTop: "10px" }}>
            <button
              className="Dashboard-btn Dashboard-btn-cancelar"
              onClick={onTornar}
            >
              Tornar
            </button>
          </div>
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {getNivellText() !== "/" && (
          <div className="info-inline">
            <strong>Nivell:</strong> {getNivellText()}
          </div>
        )}
        <div style={{ flex: 1, overflow: "auto" }}>{renderArbre(null, 0)}</div>
      </div>
    );
  };

  // =========================================================================
  //  RENDER TABS DINÀMIC (com Versions.js)
  // =========================================================================
  function renderTabs() {
    let canCreateApartat = false;
    let canCreatePartida = false;
    let canCreateSubApartat = false;
    let canCreateSubPartida = false;

    if (!selectedPartida) {
      const fills0 = partidesFills(null);
      if (fills0.length === 0) {
        canCreateApartat = true;
        canCreatePartida = true;
      } else if (hiHaApartats(null)) {
        canCreateApartat = true;
      } else if (hiHaPartides(null)) {
        canCreatePartida = true;
      }
    } else {
      const id = parseInt(selectedPartida.id);
      const isExpanded = expandedIds.includes(id);
      const pareId = selectedPartida.idPare ? parseInt(selectedPartida.idPare) : null;
      const isApartat = parseInt(selectedPartida.EsApartat) === 1;

      if (pareId === null) {
        if (!isExpanded) {
          const fills0 = partidesFills(null);
          if (fills0.length === 0) {
            canCreateApartat = true;
            canCreatePartida = true;
          } else if (hiHaApartats(null)) {
            canCreateApartat = true;
          } else if (hiHaPartides(null)) {
            canCreatePartida = true;
          }
        } else {
          const fillsSeg = partidesFills(id);
          if (hiHaApartats(id)) {
            canCreateSubApartat = true;
          } else if (hiHaPartides(id)) {
            if (isApartat) {
              canCreatePartida = true;
            } else {
              canCreateSubPartida = true;
            }
          } else {
            if (isApartat) {
              canCreateSubApartat = true;
              canCreatePartida = true;
            } else {
              canCreateSubPartida = true;
            }
          }
        }
      } else {
        if (!isExpanded) {
          const pare = partidesList.find((p) => parseInt(p.id) === pareId);
          if (pare && parseInt(pare.EsApartat) === 1) {
            if (isApartat) {
              canCreateSubApartat = true;
            } else {
              canCreatePartida = true;
            }
          } else {
            canCreateSubPartida = true;
          }
        } else {
          const fillsSeg = partidesFills(id);
          if (hiHaApartats(id)) {
            canCreateSubApartat = true;
          } else if (hiHaPartides(id)) {
            if (isApartat) {
              canCreatePartida = true;
            } else {
              canCreateSubPartida = true;
            }
          } else {
            if (isApartat) {
              canCreateSubApartat = true;
              canCreatePartida = true;
            } else {
              canCreateSubPartida = true;
            }
          }
        }
      }
    }

    if (!canCreateSubApartat && !canCreateSubPartida) {
      return (
        <div className="Dashboard-tabs">
          {canCreateApartat && (
            <button
              className={`Dashboard-tab ${activeTab === "nouApartat" ? "active" : ""}`}
              onClick={() => setActiveTab("nouApartat")}
            >
              Nou Apartat
            </button>
          )}
          {canCreatePartida && (
            <button
              className={`Dashboard-tab ${activeTab === "nou" ? "active" : ""}`}
              onClick={() => setActiveTab("nou")}
            >
              Nova Partida
            </button>
          )}
          <button
            className={`Dashboard-tab ${activeTab === "consultar" ? "active" : ""} ${!selectedPartida ? "disabled" : ""}`}
            disabled={!selectedPartida}
            onClick={() => selectedPartida && setActiveTab("consultar")}
          >
            Consultar
          </button>
          <button
            className={`Dashboard-tab ${activeTab === "modificar" ? "active" : ""} ${!selectedPartida ? "disabled" : ""}`}
            disabled={!selectedPartida}
            onClick={() => selectedPartida && setActiveTab("modificar")}
          >
            Modificar
          </button>
          <button
            className={`Dashboard-tab ${activeTab === "esborrar" ? "active" : ""} ${!selectedPartida ? "disabled" : ""}`}
            disabled={!selectedPartida}
            onClick={() => selectedPartida && setActiveTab("esborrar")}
          >
            Esborrar
          </button>
        </div>
      );
    } else if (!canCreateApartat && !canCreateSubPartida) {
      return (
        <div className="Dashboard-tabs">
          {canCreateSubApartat && (
            <button
              className={`Dashboard-tab ${activeTab === "nouSubApartat" ? "active" : ""}`}
              onClick={() => setActiveTab("nouSubApartat")}
            >
              Nou Subapartat
            </button>
          )}
          {canCreatePartida && (
            <button
              className={`Dashboard-tab ${activeTab === "nou" ? "active" : ""}`}
              onClick={() => setActiveTab("nou")}
            >
              Nova Partida
            </button>
          )}
          <button
            className={`Dashboard-tab ${activeTab === "consultar" ? "active" : ""} ${!selectedPartida ? "disabled" : ""}`}
            disabled={!selectedPartida}
            onClick={() => selectedPartida && setActiveTab("consultar")}
          >
            Consultar
          </button>
          <button
            className={`Dashboard-tab ${activeTab === "modificar" ? "active" : ""} ${!selectedPartida ? "disabled" : ""}`}
            disabled={!selectedPartida}
            onClick={() => selectedPartida && setActiveTab("modificar")}
          >
            Modificar
          </button>
          <button
            className={`Dashboard-tab ${activeTab === "esborrar" ? "active" : ""} ${!selectedPartida ? "disabled" : ""}`}
            disabled={!selectedPartida}
            onClick={() => selectedPartida && setActiveTab("esborrar")}
          >
            Esborrar
          </button>
        </div>
      );
    } else if (canCreateSubPartida) {
      return (
        <div className="Dashboard-tabs subtaula">
          <button
            className={`Dashboard-tab ${activeTab === "nouSubpartida" ? "active" : ""}`}
            onClick={() => setActiveTab("nouSubpartida")}
          >
            Nova Subpartida
          </button>
          <button
            className={`Dashboard-tab ${activeTab === "consultarSubpartida" ? "active" : ""} ${!selectedPartida ? "disabled" : ""}`}
            disabled={!selectedPartida}
            onClick={() => selectedPartida && setActiveTab("consultarSubpartida")}
          >
            Consultar
          </button>
          <button
            className={`Dashboard-tab ${activeTab === "modificarSubpartida" ? "active" : ""} ${!selectedPartida ? "disabled" : ""}`}
            disabled={!selectedPartida}
            onClick={() => selectedPartida && setActiveTab("modificarSubpartida")}
          >
            Modificar
          </button>
          <button
            className={`Dashboard-tab ${activeTab === "esborrarSubpartida" ? "active" : ""} ${!selectedPartida ? "disabled" : ""}`}
            disabled={!selectedPartida}
            onClick={() => selectedPartida && setActiveTab("esborrarSubpartida")}
          >
            Esborrar
          </button>
        </div>
      );
    }
  }

  // =========================================================================
  //  RENDER CONTINGUT ACTIU
  // =========================================================================
  const renderActiveTab = () => {
    const getPartidaPare = () => {
      if (!selectedPartida) return null;
      const expandida = expandedIds.includes(parseInt(selectedPartida.id));
      if (expandida) return selectedPartida;
      if (!selectedPartida.idPare) return null;
      return partidesList.find(
        (p) => parseInt(p.id) === parseInt(selectedPartida.idPare)
      );
    };

    const partidaPare = getPartidaPare();

    const partidaAnterior = (idPare) => {
      const mateixNivell = partidesList.filter(
        (p) => (p.idPare ? parseInt(p.idPare) : null) === idPare
      );
      if (mateixNivell.length === 0) return null;
      return mateixNivell[mateixNivell.length - 1];
    };

    switch (activeTab) {
      case "table":
        return renderArbreNivell0();

      case "nouApartat":
      case "nouSubApartat":
        return (
          <DetallsPartidesPlantilla
            mode="nouApartat"
            isApartat={true}
            plantilla={plantilla}
            partida={partidaPare}
            codiAuto={calcularSeguentPartida(
              partidaPare ? parseInt(partidaPare.id) : null
            )}
            partidesList={flatPartides}
            onNavigate={(p) => setSelectedPartida(p)}
            onSave={() => reFetchPartides()}
            onCancel={() => setActiveTab("table")}
          />
        );

      case "nou":
      case "nouSubpartida":
        return (
          <DetallsPartidesPlantilla
            mode="nou"
            isApartat={false}
            plantilla={plantilla}
            partida={partidaPare}
            codiAuto={calcularSeguentPartida(
              partidaPare ? parseInt(partidaPare.id) : null
            )}
            partidesList={flatPartides}
            onNavigate={(p) => setSelectedPartida(p)}
            onSave={() => reFetchPartides()}
            onCancel={() => setActiveTab("table")}
          />
        );

      case "consultar":
      case "consultarSubpartida":
        if (!selectedPartida) return null;
        return (
          <DetallsPartidesPlantilla
            mode="consultar"
            isApartat={parseInt(selectedPartida.EsApartat) === 1}
            plantilla={plantilla}
            partida={selectedPartida}
            partidesList={flatPartides}
            onNavigate={(p) => setSelectedPartida(p)}
            onCancel={() => setActiveTab("table")}
          />
        );

      case "modificar":
      case "modificarSubpartida":
        if (!selectedPartida) return null;
        return (
          <DetallsPartidesPlantilla
            mode="modificar"
            isApartat={parseInt(selectedPartida.EsApartat) === 1}
            plantilla={plantilla}
            partida={selectedPartida}
            partidesList={flatPartides}
            onNavigate={(p) => setSelectedPartida(p)}
            onSave={() => reFetchPartides()}
            onCancel={() => setActiveTab("table")}
          />
        );

      case "esborrar":
      case "esborrarSubpartida":
        if (!selectedPartida) return null;
        return (
          <DetallsPartidesPlantilla
            mode="esborrar"
            isApartat={parseInt(selectedPartida.EsApartat) === 1}
            plantilla={plantilla}
            partida={selectedPartida}
            partidesList={flatPartides}
            onNavigate={(p) => setSelectedPartida(p)}
            onCancel={() => {
              reFetchPartides();
            }}
          />
        );

      default:
        return renderArbreNivell0();
    }
  };

  // =========================================================================
  //  RENDER FINAL
  // =========================================================================
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "4px 0",
        }}
      >
        <strong style={{ fontSize: "1.05em" }}>
          Partides de: {plantilla.Nom}
        </strong>
        <span style={{ flex: 1 }} />
        <button
          className="Dashboard-btn Dashboard-btn-cancelar"
          onClick={onTornar}
        >
          ← Tornar a Plantilles
        </button>
      </div>

      {renderResum()}
      {renderTabs()}

      <div className="Dashboard-content">{renderActiveTab()}</div>
    </div>
  );
}

export default PartidesPlantilla;
